import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import type { Property, GalleryImage, RenderVariant, RenderSettings, WizardConfig } from '../../types/property';
import type { TransformConfig } from '../../types/analysis';
import { useGenerationRealtime } from './useGenerationRealtime';

/** Unified config type — either legacy WizardConfig or new TransformConfig */
export type GenerationConfig = WizardConfig | TransformConfig;

function isTransformConfig(config: GenerationConfig): config is TransformConfig {
  return 'actions' in config;
}

export interface CreatedImage {
  id?: string;
  publicUrl?: string;
  originalPublicUrl?: string;
  originalGcsPath?: string;
  gcsPath?: string;
  variants?: Array<{
    dbId?: string;
    id?: string;
    signedUrl?: string;
    publicUrl?: string;
    url?: string;
    gcsPath?: string;
    source?: string;
    provider?: string;
  }>;
}

interface SelectedPhotoData {
  roomName: string;
  variants: RenderVariant[];
  originalUrl?: string;
  renderSettings?: RenderSettings;
  originalGcsPath?: string;
  generationId?: string;
}

interface UseRenderGenerateParams {
  selectedProperty: Property | null;
  setSelectedProperty: (p: Property | null) => void;
  setProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  selectedPhotoData: SelectedPhotoData | null;
  setSelectedPhotoData: (data: SelectedPhotoData | null) => void;
  setIsRenderWizardOpen: (open: boolean) => void;
  setIsRenderPreviewOpen: (open: boolean) => void;
  stopGenerating: () => void;
  refreshProfile: () => void;
  activeGenerationId: string | null;
  setActiveGenerationId: (id: string | null) => void;
}

export function useRenderGenerate({
  selectedProperty,
  setSelectedProperty,
  setProperties,
  selectedPhotoData,
  setSelectedPhotoData,
  setIsRenderWizardOpen,
  setIsRenderPreviewOpen,
  stopGenerating,
  refreshProfile,
  activeGenerationId,
  setActiveGenerationId,
}: UseRenderGenerateParams) {

  // Track the config for the active generation
  const activeConfigRef = useRef<{ config: GenerationConfig; createdImage?: CreatedImage } | null>(null);
  const previewOpenedRef = useRef(false);

  // Realtime subscription for active generation
  const {
    generationStatus,
    completedVariants,
    failedVariants,
    isTimeout,
    cancel,
    retry,
  } = useGenerationRealtime(activeGenerationId, {
    onAllFailed: () => {
      toast.dismiss('generation-progress');
      toast.error('Generacja nie powiodła się. Kredyt został zwrócony.');
      refreshProfile();
      stopGenerating();
      setActiveGenerationId(null);
      activeConfigRef.current = null;
    },
  });

  // Reset preview tracking when generation changes
  useEffect(() => {
    previewOpenedRef.current = false;
  }, [activeGenerationId]);

  // Update gallery when generation finishes (not incrementally)
  useEffect(() => {
    if (!activeGenerationId || !activeConfigRef.current) return;
    if (completedVariants.length === 0) return;

    const { config, createdImage } = activeConfigRef.current;

    // Get initialImageId from either config type
    const initialImageId = isTransformConfig(config) ? undefined : config.initialImageId;
    const isNewGeneration = !initialImageId;

    // For new generations, wait until generation is fully done before adding to gallery
    if (isNewGeneration && generationStatus !== 'completed' && generationStatus !== 'failed') return;

    const renderVariants: RenderVariant[] = completedVariants.map((v, idx) => ({
      id: v.outputImageId || v.id,
      url: v.signedUrl || '',
      label: `Wariant ${String.fromCharCode(65 + idx)}`,
      gcsPath: v.gcsPath,
      source: v.aiProvider as RenderVariant['source'],
    }));

    setProperties((prevProperties) => {
      if (prevProperties.length === 0 || !selectedProperty) return prevProperties;

      const targetPropertyIndex = prevProperties.findIndex(p => p.id === selectedProperty.id);
      if (targetPropertyIndex === -1) return prevProperties;

      const updatedProperties = [...prevProperties];
      const targetProperty = { ...updatedProperties[targetPropertyIndex] };
      const newGalleryImages = [...(targetProperty.galleryImages || [])];

      // Rerender: append variants to existing image (keep incremental for rerenders)
      if (initialImageId) {
        const existingImageIndex = newGalleryImages.findIndex(img => img.id === initialImageId);
        if (existingImageIndex !== -1) {
          const existingImage = { ...newGalleryImages[existingImageIndex] };
          const currentVariantCount = existingImage.variants?.length || 0;
          const relabeledVariants = renderVariants.map((v, i) => ({
            ...v,
            label: `Wariant ${String.fromCharCode(65 + currentVariantCount + i)}`
          }));

          existingImage.variants = [...(existingImage.variants || []), ...relabeledVariants];
          existingImage.variantCount = existingImage.variants.length;
          existingImage.url = existingImage.variants[existingImage.variants.length - 1].url;
          existingImage.generationError = false;
          existingImage.generationInProgress = generationStatus === 'processing' || generationStatus === 'retrying';
          existingImage.generationId = activeGenerationId || existingImage.generationId;

          newGalleryImages[existingImageIndex] = existingImage;
          targetProperty.galleryImages = newGalleryImages;
          updatedProperties[targetPropertyIndex] = targetProperty;
          if (selectedProperty.id === targetProperty.id) setSelectedProperty(targetProperty);
          return updatedProperties;
        }
      }

      // New generation: add gallery image only when generation is complete
      const genImageId = `gen-${activeGenerationId}`;
      const genImageIndex = newGalleryImages.findIndex(img => img.id === genImageId);

      // Build render settings from config
      const renderSettings: RenderSettings = isTransformConfig(config)
        ? {
            mode: 'transform',
            actions: config.actions,
            analysisId: config.analysisId,
            roomType: config.roomType,
          }
        : {
            mode: config.mode,
            style: config.style,
            persona: config.persona,
            autoRetouch: config.autoRetouch,
            addAccessories: config.addAccessories,
          };

      const category = isTransformConfig(config) ? 'transformed' as const : config.mode;
      const configRoomName = isTransformConfig(config) ? config.roomType : config.roomName;

      const galleryImage: GalleryImage = genImageIndex >= 0
        ? { ...newGalleryImages[genImageIndex] }
        : {
            id: genImageId,
            url: '',
            category,
            aspectRatio: 'landscape' as const,
            variantCount: 0,
            roomName: configRoomName || 'Nowe Pomieszczenie',
            originalUrl: createdImage?.originalPublicUrl || createdImage?.publicUrl || '',
            deleted: false,
            favorite: false,
            variants: [],
            renderSettings,
            originalGcsPath: createdImage?.originalGcsPath || createdImage?.gcsPath,
            generationId: activeGenerationId || undefined,
          };

      galleryImage.variants = renderVariants;
      galleryImage.variantCount = renderVariants.length;
      galleryImage.url = renderVariants[renderVariants.length - 1]?.url || galleryImage.url;
      galleryImage.generationInProgress = false;

      if (genImageIndex >= 0) {
        newGalleryImages[genImageIndex] = galleryImage;
      } else {
        newGalleryImages.unshift(galleryImage);
      }

      targetProperty.galleryImages = newGalleryImages;
      targetProperty.photoCount = targetProperty.galleryImages.length;
      updatedProperties[targetPropertyIndex] = targetProperty;
      if (selectedProperty.id === targetProperty.id) setSelectedProperty(targetProperty);
      return updatedProperties;
    });
  }, [completedVariants, failedVariants, generationStatus, activeGenerationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open preview when generation finishes
  useEffect(() => {
    if (!activeGenerationId || !activeConfigRef.current) return;
    if (previewOpenedRef.current) return;
    if (generationStatus !== 'completed' && generationStatus !== 'failed') return;
    if (completedVariants.length === 0) return;

    previewOpenedRef.current = true;
    const { config, createdImage } = activeConfigRef.current;

    const renderVariants: RenderVariant[] = completedVariants.map((v, idx) => ({
      id: v.outputImageId || v.id,
      url: v.signedUrl || '',
      label: `Wariant ${String.fromCharCode(65 + idx)}`,
      gcsPath: v.gcsPath,
      source: v.aiProvider as RenderVariant['source'],
    }));

    toast.dismiss('generation-progress');

    const modeLabels: Record<string, string> = {
      enhance: 'Poprawa Jakości',
      staging: 'Wirtualne Umeblowanie',
      renovation: 'Renowacja',
      transform: 'Transformacja',
    };

    const modeLabel = isTransformConfig(config) ? 'Transformacja' : modeLabels[config.mode] || config.mode;

    if (failedVariants.length > 0 && completedVariants.length > 0) {
      toast.success('Wygenerowano częściowo', {
        description: `${completedVariants.length} z ${completedVariants.length + failedVariants.length} wariantów. Możesz ponowić nieudane.`,
      });
    } else {
      toast.success('Rendery zostały utworzone!', {
        description: `Tryb: ${modeLabel}`,
      });
    }

    stopGenerating();
    refreshProfile();

    setTimeout(() => {
      const previewInitialImageId = isTransformConfig(config) ? undefined : config.initialImageId;
      if (previewInitialImageId && selectedPhotoData) {
        const currentVariantCount = selectedPhotoData.variants?.length || 0;
        const relabeledVariants = renderVariants.map((v, i) => ({
          ...v,
          label: `Wariant ${String.fromCharCode(65 + currentVariantCount + i)}`
        }));
        setSelectedPhotoData({
          ...selectedPhotoData,
          variants: [...(selectedPhotoData.variants || []), ...relabeledVariants],
          generationId: activeGenerationId || selectedPhotoData.generationId,
        });
      } else {
        const previewRenderSettings: RenderSettings = isTransformConfig(config)
          ? {
              mode: 'transform',
              actions: config.actions,
              analysisId: config.analysisId,
              roomType: config.roomType,
            }
          : {
              mode: config.mode,
              style: config.style,
              persona: config.persona,
              autoRetouch: config.autoRetouch,
              addAccessories: config.addAccessories,
            };
        const previewRoomName = isTransformConfig(config) ? config.roomType : config.roomName;

        setSelectedPhotoData({
          roomName: previewRoomName || 'Nowe Pomieszczenie',
          variants: renderVariants,
          originalUrl: createdImage?.originalPublicUrl || createdImage?.publicUrl || '',
          renderSettings: previewRenderSettings,
          originalGcsPath: createdImage?.originalGcsPath || createdImage?.gcsPath,
          generationId: activeGenerationId || undefined,
        });
      }
      setIsRenderPreviewOpen(true);
    }, 500);

    setActiveGenerationId(null);
    activeConfigRef.current = null;
  }, [generationStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Main callback from wizard (accepts both legacy WizardConfig and new TransformConfig)
  const handleRenderGenerate = useCallback(async (config: GenerationConfig, createdImage?: CreatedImage) => {
    setIsRenderWizardOpen(false);
    toast.dismiss('generation-process');

    // Handle failed generation (orchestrator error — no generationId)
    if (config.generationFailed) {
      stopGenerating();

      setProperties((prevProperties) => {
        if (prevProperties.length === 0 || !selectedProperty) return prevProperties;

        const targetPropertyIndex = prevProperties.findIndex(p => p.id === selectedProperty.id);
        if (targetPropertyIndex === -1) return prevProperties;

        const updatedProperties = [...prevProperties];
        const targetProperty = { ...updatedProperties[targetPropertyIndex] };
        const newGalleryImages = [...(targetProperty.galleryImages || [])];

        const failedCategory = isTransformConfig(config) ? 'transformed' as const : config.mode;
        const failedRoomName = isTransformConfig(config) ? config.roomType : config.roomName;
        const failedRenderSettings: RenderSettings = isTransformConfig(config)
          ? { mode: 'transform', actions: config.actions, analysisId: config.analysisId, roomType: config.roomType }
          : { mode: config.mode, style: config.style, persona: config.persona, autoRetouch: config.autoRetouch, addAccessories: config.addAccessories };

        const failedPhoto: GalleryImage = {
          id: createdImage?.id || `failed-${Date.now()}`,
          url: createdImage?.originalPublicUrl || createdImage?.publicUrl || '',
          category: failedCategory,
          aspectRatio: 'landscape' as const,
          variantCount: 0,
          roomName: failedRoomName || 'Nowe Pomieszczenie',
          originalUrl: createdImage?.originalPublicUrl || createdImage?.publicUrl || '',
          deleted: false,
          favorite: false,
          variants: [],
          renderSettings: failedRenderSettings,
          originalGcsPath: createdImage?.originalGcsPath || createdImage?.gcsPath,
          generationError: true,
        };

        targetProperty.galleryImages = [failedPhoto, ...newGalleryImages];
        targetProperty.photoCount = targetProperty.galleryImages.length;
        updatedProperties[targetPropertyIndex] = targetProperty;

        if (selectedProperty.id === targetProperty.id) {
          setSelectedProperty(targetProperty);
        }
        return updatedProperties;
      });

      refreshProfile();
      return;
    }

    // Async generation — store config and activate Realtime subscription
    if (config.generationId) {
      activeConfigRef.current = { config, createdImage };
      setActiveGenerationId(config.generationId);

      toast.loading('Generowanie renderów AI...', {
        id: 'generation-progress',
        description: 'Warianty pojawią się automatycznie',
      });
      return;
    }

    // Fallback: shouldn't happen
    stopGenerating();
  }, [selectedProperty, setProperties, setSelectedProperty, setIsRenderWizardOpen, stopGenerating, refreshProfile, setActiveGenerationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    handleRenderGenerate,
    activeGenerationId,
    generationStatus,
    completedVariants,
    failedVariants,
    isTimeout,
    cancelGeneration: cancel,
    retryGeneration: retry,
  };
}
